import { useState } from "react"
import { observer } from "mobx-react-lite"

import type { TokenName } from "@/domains/core"
import { DialogSearch } from "@/domains/common/components/dialogues/dialog-search"
import { TokenStore } from "@/domains/tokens"
import {
  filterListedTokens,
  filterTokensBySearch,
  filterUnlistedTokens,
  sortTokensByBalanceValueUSD,
} from "@/domains/tokens/utils/filter-tokens"

import type { TokenDialogTab } from "./tabs"
import { TokenStoreDialog } from "../token-dialog"
import { TokenList } from "../token-list"
import { TokenDialogLocalTokenManager } from "./local-token-manager"
import { TokenDialogQuickSelect } from "./quick-select"
import { TokenDialogTabs } from "./tabs"
import { TokenDialogWarningMessage } from "./warning-message"

export const SwapTokenDialog: React.FC<{
  isOpen: boolean
  tokens: Array<TokenStore>
  quickSelectTokens: Array<TokenStore>
  governanceToken: TokenName
  nativeToken: TokenStore
  wrappedNativeToken: TokenStore
  onClose: () => void
  onTokenSelect: (token: TokenStore | null) => void
  filterTokens?: (token: TokenStore) => boolean
  showLocalAssets?: boolean
  localTokens?: Array<TokenStore>
  onAddLocalToken?: (address: string) => Promise<void>
  onRemoveLocalToken?: (address: string) => void
}> = observer(
  ({
    isOpen,
    tokens,
    quickSelectTokens,
    governanceToken,
    nativeToken,
    wrappedNativeToken,
    onClose,
    onTokenSelect,
    filterTokens,
    showLocalAssets = false,
    localTokens = [],
    onAddLocalToken,
    onRemoveLocalToken,
  }) => {
    const [activeTab, setActiveTab] = useState("listed")
    const [search, setSearch] = useState("")

    const filteredTokens = filterTokens ? tokens.filter(filterTokens) : tokens
    const listedTokens = sortTokensByBalanceValueUSD(filterListedTokens(filteredTokens, nativeToken))
    const unlistedTokens = sortTokensByBalanceValueUSD(filterUnlistedTokens(filteredTokens, nativeToken))
    const searchListedTokens = filterTokensBySearch(listedTokens, search, nativeToken.symbol, wrappedNativeToken.symbol)
    const searchUnlistedTokens = filterTokensBySearch(unlistedTokens, search, nativeToken.symbol, wrappedNativeToken.symbol)

    const tabs: Array<TokenDialogTab> = showLocalAssets
      ? [
          {
            id: "listed",
            label: "Listed",
            content: (
              <>
                <DialogSearch
                  value={search}
                  onChange={setSearch}
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                />
                <TokenDialogQuickSelect tokens={quickSelectTokens} onTokenSelect={onTokenSelect} onClose={onClose} />
                <TokenList tokens={searchListedTokens} onTokenSelect={onTokenSelect} onClose={onClose} />
              </>
            ),
          },
          {
            id: "imported",
            label: "Imported",
            content: (
              <TokenDialogLocalTokenManager
                tokens={localTokens}
                onTokenSelect={onTokenSelect}
                onAddToken={onAddLocalToken}
                onRemoveToken={onRemoveLocalToken}
                onClose={onClose}
              />
            ),
          },
          {
            id: "unlisted",
            label: "Unlisted",
            content: (
              <>
                <DialogSearch
                  value={search}
                  onChange={setSearch}
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                />
                <TokenDialogWarningMessage message="Unlisted tokens are possibly unsafe. Anyone can create a token via our Liquidity Pools, use at your own risk. Please contact the team if your token appears here incorrectly." />
                <TokenList
                  tokens={searchUnlistedTokens}
                  onTokenSelect={onTokenSelect}
                  onClose={onClose}
                  maxHeight={280}
                />
              </>
            ),
          },
        ]
      : [
          {
            id: "listed",
            label: "Listed",
            content: (
              <>
                <DialogSearch
                  value={search}
                  onChange={setSearch}
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                />
                <TokenDialogQuickSelect tokens={quickSelectTokens} onTokenSelect={onTokenSelect} onClose={onClose} />
                <TokenList tokens={searchListedTokens} onTokenSelect={onTokenSelect} onClose={onClose} />
              </>
            ),
          },
          {
            id: "unlisted",
            label: "Unlisted",
            content: (
              <>
                <TokenDialogWarningMessage message="Unlisted tokens are possibly unsafe. Anyone can create a token via our Liquidity Pools, use at your own risk. Please contact the team if your token appears here incorrectly." />
                <DialogSearch
                  value={search}
                  onChange={setSearch}
                  placeholder={`${governanceToken.name}, ${nativeToken.name}, BTC, ETH, 0x...`}
                />
                <TokenList tokens={searchUnlistedTokens} onTokenSelect={onTokenSelect} onClose={onClose} />
              </>
            ),
          },
        ]

    return (
      <TokenStoreDialog isOpen={isOpen} onClose={onClose} title="Select Token">
        <TokenDialogTabs tabs={tabs} value={activeTab} onValueChange={setActiveTab} />
      </TokenStoreDialog>
    )
  },
)
